import type { PromptType } from "@/schema/prompt"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import { createPromptRun, updatePrompt, type PromptUpdateParams } from "./api"

export function useUpdatePrompt(bookId?: string) {
  const getContext = useApiContext(bookId)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      contentJson,
      settings,
    }: {
      id: string
      contentJson?: Record<string, string>
      settings?: PromptUpdateParams["settings"]
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updatePrompt(context, id, { contentJson, settings })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prompts"] })
      queryClient.invalidateQueries({ queryKey: ["prompt"] })
    },
  })
}

export function useCreatePromptRun() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ promptableId, promptType }: { promptableId: string; promptType: PromptType }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createPromptRun(context, promptableId, promptType)
    },
    onSuccess: (_data, { promptType, promptableId }) => {
      if (promptType.startsWith("Prompt::Meeting")) {
        queryClient.invalidateQueries({ queryKey: ["meeting"] })
        queryClient.invalidateQueries({ queryKey: ["meeting", promptableId] })
        queryClient.invalidateQueries({ queryKey: ["meetings"] })
        queryClient.invalidateQueries({ queryKey: ["meeting", promptableId, "tasks"] })
        return
      }

      if (promptType === "Prompt::HouseholdSummary") {
        queryClient.invalidateQueries({ queryKey: ["household"] })
        queryClient.invalidateQueries({ queryKey: ["household", promptableId] })
        queryClient.invalidateQueries({ queryKey: ["clients"] })
        queryClient.invalidateQueries({ queryKey: ["prospects"] })
      }
    },
  })
}
